import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableBarbers } from '@/hooks/useAvailableBarbers';
import { useBarberWaitTime } from '@/hooks/useBarberWaitTime';
import { useBranch } from '@/contexts/BranchContext';
import { Barber, BarberStatus, BreakReason, BREAK_REASON_LABELS, BranchSchedule } from '@/types';
import { pendingBreakMinutes, isBreakActive, isBreakScheduled, breakStartLabel } from '@/lib/breaks';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Coffee, Clock, CalendarDays, UserCheck, Dices, Loader2, Check, Zap } from 'lucide-react';

interface BarberSelectionProps {
  onSelectBarber: (barber: Barber | { id: string; displayName: string }) => void;
  branchId?: string;
}

interface BarberCardProps {
  barber: Barber;
  branchId: string;
  onSelect: () => void;
  getInitials: (name: string) => string;
}

// BarberCard component with real-time wait time and status
function BarberCard({ barber, branchId, onSelect, getInitials }: BarberCardProps) {
  const { t } = useTranslation();
  const [barberStatus, setBarberStatus] = useState<BarberStatus | null>(null);

  const { totalWaitSeconds, waitingCount } = useBarberWaitTime({
    barberId: barber.barberId || barber.userId,
    branchId,
    enabled: true,
  });

  // Subscribe to barber status
  useEffect(() => {
    const barberId = barber.barberId || barber.userId;
    if (!barberId) return;

    const statusRef = doc(db, 'barberStatus', barberId);
    const unsubscribe = onSnapshot(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        setBarberStatus(snapshot.data() as BarberStatus);
      } else {
        setBarberStatus(null);
      }
    });

    return () => unsubscribe();
  }, [barber.barberId, barber.userId]);

  // Format seconds to HH:MM:SS or MM:SS for display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  // Cuánto le queda de pausa, solo mientras la esté cumpliendo
  const getReturnTimeText = () => {
    const min = pendingBreakMinutes(barberStatus);
    if (min <= 0) return 'Volviendo pronto';
    return `Vuelve en ${min} min`;
  };

  // Check if barber is on a break
  const isDayOff = barberStatus?.breakReason === 'day_off';
  // #22: el profesional marca la comida por anticipado, mientras sigue cortando.
  // Hasta que la pausa empieza de verdad no se dice «Comiendo»: se indica la
  // hora a la que para, y el tiempo de espera de arriba ya la incluye.
  const isTemporaryBreak = isBreakActive(barberStatus);
  const breakPending = isBreakScheduled(barberStatus);
  const breakStart = breakStartLabel(barberStatus);

  // If day off, don't show the barber
  if (isDayOff) {
    return null;
  }

  return (
    <button
      onClick={onSelect}
      className="group bg-surface rounded-xl p-5 border border-hairline hover:border-accent/60 transition-colors text-left"
    >
      {/* Barber Photo/Initials */}
      <div className="mb-4 flex justify-center">
        {barber.photoURL ? (
          <img
            src={barber.photoURL}
            alt={barber.displayName}
            className="w-20 h-20 rounded-full object-cover ring-1 ring-hairline"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-surface-3 flex items-center justify-center ring-1 ring-hairline">
            <span className="text-2xl font-semibold text-ink-muted">
              {getInitials(barber.displayName)}
            </span>
          </div>
        )}
      </div>

      {/* Barber Info */}
      <h3 className="font-sans text-lg font-semibold text-ink mb-3 text-center">
        {barber.displayName}
      </h3>

      {/* Wait Time Badge - Real-time updating */}
      {/*
        Se muestra el tiempo si hay cola O si hay espera por otra causa (un
        descanso temporal en curso). Antes la condición era solo `waitingCount > 0`,
        así que un barbero comiendo con la cola vacía aparecía como "Sin cola /
        Disponible ahora" y ocultaba los minutos que faltaban para su vuelta: el
        cliente se presentaba y se lo encontraba almorzando.
      */}
      {waitingCount > 0 || totalWaitSeconds > 0 ? (
        <div className="mb-3 p-3 bg-surface-2 rounded-lg border border-hairline text-center">
          <p className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-subtle mb-1">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {t('booking.barberSelection.waitTime')}
          </p>
          <p className="text-2xl font-semibold text-ink tabular-nums">
            {formatTime(totalWaitSeconds)}
          </p>
          {waitingCount > 0 && (
            <p className="text-sm text-ink-muted mt-1">
              {waitingCount} {waitingCount === 1 ? t('booking.barberSelection.clientInQueue') : t('booking.barberSelection.clientsInQueue')}
            </p>
          )}
        </div>
      ) : (
        <div className="mb-3 py-2 text-center">
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-success">
            <Check className="w-4 h-4" aria-hidden="true" /> {t('booking.barberSelection.noQueue')}
          </p>
        </div>
      )}

      {/* Specialties */}
      {barber.specialties && barber.specialties.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center mt-3">
          {barber.specialties.slice(0, 2).map((specialty, idx) => (
            <span
              key={idx}
              className="bg-surface-3 text-ink-muted px-2 py-1 rounded-full text-xs"
            >
              {specialty}
            </span>
          ))}
          {barber.specialties.length > 2 && (
            <span className="bg-surface-3 text-ink-muted px-2 py-1 rounded-full text-xs">
              +{barber.specialties.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Availability Badge */}
      <div className="mt-4 text-center">
        {isTemporaryBreak ? (
          <div className="space-y-2">
            <div className="inline-flex items-center justify-center gap-2 bg-warning/10 text-warning px-3 py-1.5 rounded-lg">
              <Coffee size={16} />
              <span className="text-sm font-medium">
                {BREAK_REASON_LABELS[barberStatus?.breakReason as BreakReason] || 'En pausa'}
              </span>
            </div>
            {barberStatus?.breakExpectedEndAt && (
              <div className="flex items-center justify-center gap-1 text-xs text-ink-subtle">
                <Clock size={12} />
                <span>{getReturnTimeText()}</span>
              </div>
            )}
          </div>
        ) : breakPending && breakStart ? (
          <div className="flex items-center justify-center gap-1.5 text-xs text-ink-muted">
            <Coffee size={13} aria-hidden="true" />
            <span>Para a las {breakStart}</span>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
            <Check className="w-3.5 h-3.5" aria-hidden="true" /> {t('booking.barberSelection.available')}
          </span>
        )}
      </div>
    </button>
  );
}

export function BarberSelection({ onSelectBarber, branchId }: BarberSelectionProps) {
  const { t } = useTranslation();
  const { availableBarbers, loading } = useAvailableBarbers(branchId);
  const { currentBranch } = useBranch();

  // Helper function to get initials from name
  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return name.substring(0, 2).toUpperCase();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-ink-subtle animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (availableBarbers.length === 0) {
    const schedule = currentBranch?.schedule;
    const dayKeys: (keyof BranchSchedule)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = [
      t('booking.barberSelection.days.monday'),
      t('booking.barberSelection.days.tuesday'),
      t('booking.barberSelection.days.wednesday'),
      t('booking.barberSelection.days.thursday'),
      t('booking.barberSelection.days.friday'),
      t('booking.barberSelection.days.saturday'),
      t('booking.barberSelection.days.sunday'),
    ];

    return (
      <div className="text-center py-8 px-4">
        <CalendarDays className="w-8 h-8 text-ink-subtle mx-auto mb-3" aria-hidden="true" />
        <p className="text-base font-semibold text-ink mb-1">
          {t('booking.barberSelection.empty')}
        </p>
        <p className="text-sm text-ink-muted mb-6">
          {t('booking.barberSelection.emptySubtitle')}
        </p>

        {schedule && (
          <div className="bg-surface rounded-xl p-5 max-w-sm mx-auto border border-hairline">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CalendarDays className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
              <h3 className="font-sans font-semibold text-ink text-sm uppercase tracking-wide">
                {t('booking.barberSelection.scheduleTitle')}
              </h3>
            </div>
            <div className="space-y-1">
              {dayKeys.map((day, i) => {
                const daySchedule = schedule[day];
                return (
                  <div
                    key={day}
                    className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg"
                  >
                    <span className="text-ink-muted">
                      {dayLabels[i]}
                    </span>
                    {daySchedule?.isOpen ? (
                      <span className="text-ink font-medium tabular-nums">
                        {daySchedule.open} – {daySchedule.close}
                      </span>
                    ) : (
                      <span className="text-ink-subtle text-sm">
                        {t('booking.barberSelection.closed')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-sans text-xl font-semibold tracking-tight text-ink mb-1 flex items-center justify-center gap-2">
        <UserCheck className="w-5 h-5 shrink-0 text-ink-subtle" aria-hidden="true" />
        {t('booking.barberSelection.title')}
      </h2>
      <p className="text-sm text-ink-muted text-center mb-6">
        {t('booking.barberSelection.subtitle')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Option 1: "Cualquiera" - Auto-assign to barber with shortest queue.
            The only accent-bordered card: it's the recommended fast path. */}
        <button
          onClick={() => onSelectBarber({ id: 'any', displayName: t('booking.barberSelection.anyBarber') })}
          className="group bg-surface rounded-xl p-5 border border-accent/50 hover:border-accent transition-colors text-left"
        >
          {/* Icon */}
          <div className="mb-4 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center">
              <Dices className="w-9 h-9 text-accent" aria-hidden="true" />
            </div>
          </div>

          {/* Info */}
          <h3 className="font-sans text-lg font-semibold text-ink mb-1 text-center">
            {t('booking.barberSelection.anyBarber')}
          </h3>
          <p className="text-sm text-ink-muted text-center">
            {t('booking.barberSelection.anyBarberDescription')}
          </p>

          {/* Badge */}
          <div className="mt-4 text-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent">
              <Zap className="w-3.5 h-3.5" aria-hidden="true" /> {t('booking.barberSelection.fastest')}
            </span>
          </div>
        </button>

        {/* Available Barbers */}
        {availableBarbers.map((barber) => (
          <BarberCard
            key={barber.barberId}
            barber={barber}
            branchId={branchId || ''}
            onSelect={() => onSelectBarber(barber)}
            getInitials={getInitials}
          />
        ))}
      </div>
    </div>
  );
}
